﻿import { DataGrid, EditorTypeRegistry, EntityGrid, ToolButton, Widget } from "@serenity-is/corelib";
import { deepClone, Dictionary, extend, getHighlightTarget, isBS3, notifyError, notifyWarning, PropertyItem, PropertyItemsData, SaveRequest, serviceRequest, tryFirst, tryGetText } from "@serenity-is/corelib/q";
import { CellStylesHash, Column, EditorClass, EditorFactory, EditorOptions, RowCell } from "@serenity-is/sleekgrid";
import { GridEditWrapper } from "./GridEditWrapper";

export interface GridEditOptions<TItem = any> {
    grid: DataGrid<TItem, any>;
    autoSave?: boolean;
    bulkSaveHandler?: (reqs: SaveRequest<TItem>[]) => Promise<void>;
    editable?: (args: { column: Column<TItem>, item: TItem, row: number }) => boolean;
    editorParams?: object | ((args: { column: Column<TItem>, item: TItem, slickArgs: EditorOptions }) => any);
    editorType?: (args: { column: Column<TItem>, item: TItem, slickArgs: EditorOptions }) => {
        new (...args: any[]): Widget;
    },
    editorInit?: ((args: { editor: Widget, column: Column<TItem>, item: TItem, slickArgs: EditorOptions }) => void);
    afterCommitEdit?: ((args: { column: Column<TItem>, item: TItem, value: any, oldValue: any, row: number }) => void);
    refreshAfterSave?: boolean;
    resetOnLoad?: boolean;
    saveHandler?: (req: SaveRequest<TItem>) => Promise<void>;
    saveServiceMethod?: string;
    showValidationError?: boolean;
    undoRowDblEsc?: boolean;
    validator?: (value: any, args: EditorOptions) => string;
}

const defaultGridEditOptions: Partial<GridEditOptions> = {
    autoSave: false,
    refreshAfterSave: true,
    resetOnLoad: true,
    showValidationError: true,
    undoRowDblEsc: true
} as const;

export class GridEditController<TItem = any> implements EditorFactory {

    private _options: GridEditOptions<TItem>;
    private dirtyStyles: CellStylesHash = {};

    constructor(opt: GridEditOptions<TItem>) {
        this._options = opt = extend(extend({}, defaultGridEditOptions), opt) as GridEditOptions;

        opt.grid.slickGrid.setOptions({
            editable: true,
            editorFactory: this,
            enableCellNavigation: true,
            editorCellNavOnLRKeys: false,
            autoEdit: true,
            editCommandHandler: (item: any, column, command) => {
                var orig = item.__orig;
                if (orig == null)
                    item.__orig = extend({}, item);
                var oldValue = item[column.field];
                command.execute();
                var value = item[column.field];
                this.options.afterCommitEdit?.({
                    column: column,
                    item,
                    oldValue,
                    value,
                    row: command.row
                });
                this.updateModifiedClassesForRow(command.row);
                opt.grid["updateInterface"]();

                if (opt.autoSave)
                    this.saveChanges();
            }
        });

        var priorEscCell: RowCell;

        if (opt.undoRowDblEsc) {
            // cancel changes for current row on double escape<
            opt.grid.slickGrid.onKeyDown.subscribe(e => {
                if (e.key === "Escape" &&
                    !opt.grid.slickGrid.getCellEditor()) {
                    var activeCell = opt.grid.slickGrid.getActiveCell();
                    if (activeCell != null &&
                        activeCell.row >= 0 &&
                        activeCell.cell >= 0 &&
                        priorEscCell != null &&
                        activeCell.row == priorEscCell.row &&
                        activeCell.cell == priorEscCell.cell) {
                        priorEscCell = null;
                        this.undoRowChanges(activeCell.row);
                        opt.grid.slickGrid.updateRow(activeCell.row);
                    }
                    else
                        priorEscCell = activeCell;
                }
                else
                    priorEscCell = null;
            });
        }

        if (opt.showValidationError) {
            opt.grid.slickGrid.onValidationError.subscribe((e, args) => {
                showValidationError(args.cellNode, args.validationResults.msg);
            });
        }

        if (opt.resetOnLoad) {
            opt.grid.view?.onDataLoaded.subscribe(() => {
                this.undoChanges();
            });
        }
    }

    updateModifiedClassesForRow(row: number) {
        var slick = this._options.grid.slickGrid;
        var item = slick.getDataItem(row);
        var orig = (item as any).__orig;
        if (orig == null)
            delete this.dirtyStyles[row];
        else {
            var styles = this.dirtyStyles[row];
            if (styles == null)
                this.dirtyStyles[row] = styles = {};
            var cols = slick.getColumns();
            for (var cell = 0; cell < cols.length; cell++) {
                var col = cols[cell];
                var field = col.editableProperty?.name ?? col.field;
                if (field == null ||
                    !this.isFieldDirty(item, field))
                    delete styles[col.id];
                else
                    styles[col.id] = "slick-cell-dirty";
            }
        }
        slick.setCellCssStyles("dirtycells", deepClone(this.dirtyStyles));
    }

    get options() {
        return this._options;
    }

    getEditor(column: Column<any>, row: number): EditorClass {

        if (column.editableProperty == void 0 &&
            column.sourceItem &&
            column.sourceItem.filteringIdField != null) {
            var pid = this._options.grid["propertyItemsData"] as PropertyItemsData;
            if (pid != null) {
                var idField = column.sourceItem.filteringIdField;
                column.editableProperty = tryFirst(pid.items || [], x => x.name == idField) ??
                    tryFirst(pid.additionalItems || [], x => x.name == idField) ?? null;
            }
        }

        var propertyItem = column.editableProperty ?? column.sourceItem;

        if (propertyItem == null ||
            propertyItem.readOnly ||
            propertyItem.updatable === false ||
            EditorTypeRegistry.get(column.sourceItem.editorType ?? "String") == null)
            return null;
    
        if (this._options.editable != null) {
            var item = this._options.grid.slickGrid.getDataItem(row);
            var editable = this._options.editable({ column, item, row });
            if (editable !== void 0 && !editable)
                return null;
        }
       
        return GridEditWrapper;
    }

    isDirty() {
        var slick = this._options.grid?.slickGrid;
        if (!slick)
            return false;
        var dataLength = slick.getDataLength();
        for (var i = 0; i < dataLength; i++) {
            var item = slick.getDataItem(i);
            if (this.isItemDirty(item))
                return true;
        }
        return false;
    }

    isItemDirty(item: any) {
        return item && item.__orig &&
            Object.keys(item).some(key => key !== "__orig" && item.__orig[key] != item[key]);
    }

    isFieldDirty(item: any, field: string) {
        return item.__orig && (item.__orig[field] != item[field]);
    }

    undoRowChanges(row: number) {
        var item = this._options.grid.slickGrid.getDataItem(row);
        if (item && item.__orig) {
            var orig = item.__orig;
            for (var i in item)
                delete item[i];
            for (var i in orig)
                item[i] = orig[i];
        }
        this.updateModifiedClassesForRow(row);
    }

    undoChanges() {
        var slick = this._options.grid?.slickGrid;
        if (!slick)
            return;
        var dataLength = slick.getDataLength();
        for (var row = 0; row < dataLength; row++) {
            var item = slick.getDataItem(row);
            if (item.__orig)
                this.undoRowChanges(row);
        }
        for (var key in this.dirtyStyles)
            delete this.dirtyStyles[key];
        slick.setCellCssStyles("dirtycells", deepClone(this.dirtyStyles));
        slick.invalidate();
    }

    saveChanges() {

        var slick = this._options.grid?.slickGrid;
        if (!slick)
            return;

        var dataLength = slick.getDataLength();
        var requests: Dictionary<SaveRequest<TItem>> = {};

        for (var row = 0; row < dataLength; row++) {
            var item = slick.getDataItem(row);
            if (this.isItemDirty(item)) {
                var request: SaveRequest<TItem> = {
                    EntityId: item.__orig[this._options.grid["getIdProperty"]()],
                    Entity: Object.create(null) as TItem
                };
                for (var field of Object.keys(item)) {
                    if (field === "__orig")
                        continue;
                    if (this.isFieldDirty(item, field))
                        request.Entity[field] = item[field];
                }
                if (Object.keys(request.Entity as any).length)
                    requests[row] = request;
            }
        }

        var rows = Object.keys(requests);
        var current = -1;
        var self = this;

        (function saveNext() {
            if (++current >= rows.length) {
                self.undoChanges();
                if (self._options.refreshAfterSave)
                    self._options.grid.refresh();
                self._options.grid["updateInterface"]();
                return;
            }

            var row = parseInt(rows[current], 10);
            var request = requests[row];
            var item = self._options.grid.slickGrid.getDataItem(row);

            const then = () => {
                delete item.__orig;
                delete request[row];
                self.updateModifiedClassesForRow(row);
                saveNext();
            }

            const fail = () => {
                if (self._options.autoSave)
                    self.undoRowChanges(row);
                self._options.grid["updateInterface"]();
            }

            if (self._options.saveHandler) {
                self._options.saveHandler(request)
                    .then(then)
                    .catch(fail);
            }
            else {
                var serviceMethod = self._options.saveServiceMethod;

                if (serviceMethod == null) {
                    var getService = (self._options.grid as EntityGrid<any, any>)["getService"];
                    if (getService)
                        serviceMethod = getService() + "/Update";
                }

                if (serviceMethod)
                    serviceRequest(serviceMethod, request, then)
                        .fail(fail);
                else
                    notifyWarning("Save handler for edit controller is not defined!");
            }
        })();
    }

    static saveChangesButton(controller: () => GridEditController): ToolButton {
        return {
            title: tryGetText('Controls.EntityGrid.SaveChangesButton') ?? "Save Changes",
            cssClass: 'apply-changes-button',
            disabled: () => !controller()?.isDirty(),
            onClick: () => controller()?.saveChanges(),
            separator: true
        }
    }

    static undoButton(controller: () => GridEditController): ToolButton {
        return {
            hint: tryGetText('Controls.EntityGrid.UndoChangesButton') ?? "Undo Changes",
            cssClass: 'undo-changes-button',
            icon: 'fa-undo',
            disabled: () => !controller()?.isDirty(),
            onClick: e => controller()?.undoChanges()
        }
    }
}

function showValidationError(el: HTMLElement, msg) {
    if ($.fn.tooltip) {
        var $el: any;
        var hl = getHighlightTarget(el);
        if (hl)
            $el = $(hl);
        else
            $el = $(el);

        $.fn.tooltip && $el.tooltip({
            title: msg,
            trigger: 'manual'
        }).tooltip('show');

        window.setTimeout(function () {
            $el.tooltip(isBS3() ? 'destroy' : 'dispose');
        }, 1500);
    }
    else
        notifyError(msg);
}

declare module "@serenity-is/sleekgrid" {
    interface Column<TItem = any> {
        editableProperty?: PropertyItem;
    }
}